import * as React from 'react';
import { createStyles, Radio, Group, RadioProps, NumberInput } from '@mantine/core';
import { useInputState } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

// Styles
import { getCommonFieldStyles } from '../helpers/fieldStyles';

// Hooks
import { useFieldTranslation } from '../hooks/useFieldTranslation';

// Components
import { HelpTooltipButton } from '../../HelpTooltipButton';

// Types
import { GenericFieldProps } from '../types';

const useStyles = createStyles((theme) => {
    const commonFieldStyles = getCommonFieldStyles(theme);

    return {
        root: commonFieldStyles.root,
        input: commonFieldStyles.input,
        readonlyInput: commonFieldStyles.readonlyInput,
        inputHighlight: commonFieldStyles.inputHighlight,
        labelHighlight: commonFieldStyles.labelHighlight,
        label: commonFieldStyles.label,
        icon: commonFieldStyles.icon,
        error: commonFieldStyles.error,
        wrapper: commonFieldStyles.wrapper,
        readonlyWrapper: commonFieldStyles.readonlyWrapper,
        description: commonFieldStyles.description,
        radioGroup: {
            minHeight: '2.3rem',
            alignItems: 'center'
        }
    };
});

export interface CustomNumberOrNoAnswerRadioGroupProps extends Omit<RadioProps, 'onChange'>, GenericFieldProps {
    key?: string;
    value?: number;
    onChange?: (val: number) => void;
}
enum RadioValues {
    dont_know = 'idk',
    decline_to_answer = 'dna',
    number_provided = 'number_provided'
}
export const ValueToRadio = {
    '-1': RadioValues.dont_know,
    '-2': RadioValues.decline_to_answer
};
const interpretValue = (value?: number) => {
    if (typeof value === 'undefined') return;
    return value >= 0 ? RadioValues.number_provided : (ValueToRadio as any)[value.toString()] ?? undefined;
};

export const NumberOrNoAnswerRadioGroup: React.FC<CustomNumberOrNoAnswerRadioGroupProps> = ({
    readOnly,
    labelKey,
    label,
    parentform,
    targetperiod,
    highlight,
    ...props
}) => {
    const { t } = useTranslation('common');
    const { cx, classes } = useStyles();
    const inputLabel = useFieldTranslation({
        key: labelKey,
        label,
        formData: parentform?.values,
        targetPeriod: targetperiod
    });
    const [value, setValue] = React.useState(interpretValue(props?.value));
    const [numberValue, setNumberValue] = useInputState(
        typeof props?.value !== 'undefined' && props.value >= 0 ? props.value : undefined
    );
    const onChangeNumber = React.useCallback(
        (val: number) => {
            setNumberValue(Math.abs(val));
            props?.onChange?.(Math.abs(val));
        },
        [props]
    );
    React.useEffect(() => {
        setValue(interpretValue(props?.value));
    }, [props?.value]);

    React.useEffect(() => {
        if (value !== RadioValues.number_provided) {
            setNumberValue(0);
        }
    }, [value]);
    const onChangeRadio = React.useCallback((val: string) => {
        setValue(val);
        const [key] = Object.entries(ValueToRadio).find(([key, value]) => value === val) ?? [];

        if (key) {
            props?.onChange?.(Number(key));
        } else {
            props?.onChange?.(0);
        }
    }, []);

    return (
        <Radio.Group
            label={inputLabel}
            name={props.prefix}
            value={value}
            onChange={onChangeRadio}
            required={props.required}
            error={props.error}
            classNames={{
                label: cx(classes.label, {
                    [classes.labelHighlight]: typeof highlight === 'string' && highlight === 'true'
                })
            }}
        >
            <Group mt="xs" className={classes.radioGroup}>
                <NumberInput
                    value={numberValue}
                    onClick={() => {
                        setValue(RadioValues.number_provided);
                        console.log('numberValue', numberValue ?? 0);
                        props?.onChange?.(numberValue ?? 0);
                    }}
                    onFocus={() => {
                        props?.onChange;
                    }}
                    onChange={onChangeNumber}
                    hideControls
                    placeholder={'0'}
                />
                <HelpTooltipButton
                    labelKey={'NumberOrNoAnswerRadioGroup.tooltipLabelKey'}
                    translationNamespace={'components'}
                />
            </Group>
            <Group mt="xs" className={classes.radioGroup}>
                <Radio
                    value={RadioValues.dont_know}
                    label={t('dontKnow')}
                    id={`${props.prefix}_dont_know`}
                    onClick={(e) => {
                        if (value === RadioValues.dont_know) {
                            // e.preventDefault();
                            setValue(null);
                        }
                    }}
                />
                <Radio
                    value={RadioValues.decline_to_answer}
                    label={t('preferNotToAnswer')}
                    id={`${props.prefix}_no_answer`}
                    onClick={(e) => {
                        if (value === RadioValues.decline_to_answer) {
                            // e.preventDefault();
                            setValue(null);
                        }
                    }}
                />
            </Group>
        </Radio.Group>
    );
};
